import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  CONSTANTS as Q_CONSTANTS,
  ethnicityMap
} from 'src/components/questionnaire/Constants';
import { resolveVitalStatus } from '../pedigree-utils';
import ClinicalRecordsPanel from './ClinicalRecordsPanel';
import { useTheme } from '@mui/material/styles';
import { ChangedField } from 'src/components/UI/input/changed-field';
import { changedPalette } from 'src/theme/palette';
import {
  changedPersonFields,
  normaliseGenderIdentity,
  savedPersonValue
} from './useBulkEditState';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { english } from 'src/i18n/language';

const inputStyle = {
  border: '1px solid #ccc',
  borderRadius: 4,
  padding: '4px 6px',
  fontSize: 13,
  width: '100%',
  height: 32,
  boxSizing: 'border-box',
  fontFamily: 'inherit'
};

const COLUMN_DRAFT_KEYS = {
  firstName: ['firstName'],
  lastName: ['lastName'],
  gender: ['genderIdentity'],
  status: ['isAlive'],
  dob: ['dob'],
  dod: ['dod'],
  ethnicity: ['ethnicity'],
  mobile: ['mobile'],
  email: ['email'],
  hospitalNumber: ['hospitalNumber'],
  healthcareId: ['healthcareId'],
  isHide: ['isHide'],
  recordsReviewed: ['recordsReviewed']
};

// The columns whose control has no outline of its own: drawn round it.
const CONTROL_COLUMNS = new Set(['isHide', 'recordsReviewed']);

// A plain <input> is not one of MUI's, so ChangedField's input styles miss it:
// its ground and border take the gold here (TGCLOUD-1902). The browser's focus
// ring still shows over it.
const changedInputStyle = (palette, unsaved) =>
  unsaved
    ? {
        ...inputStyle,
        border: `1px solid ${palette.main}`,
        backgroundColor: palette.bg
      }
    : inputStyle;

const STATUS_OPTIONS = [
  { value: 'true', label: 'Alive' },
  { value: 'false', label: 'Deceased' }
];

const GENDER_IDENTITY_SHORT = {
  Male: 'M',
  Female: 'F',
  Indeterminate: 'Ind',
  'Non-binary/gender diverse': 'NB',
  'Unknown/Not Stated': 'Unk'
};

function shortGenderLabel(value) {
  const normalised = normaliseGenderIdentity(value);
  if (!normalised) return '';
  const [identity, assignedAtBirth] = normalised.split(' - ');
  const head = GENDER_IDENTITY_SHORT[identity.trim()] ?? identity.trim();
  return assignedAtBirth ? `${head}·${assignedAtBirth.trim()}` : head;
}

// An option's label is ours and translated; a stored value no option holds is
// shown as it was recorded.
function fullGenderLabel(value, t = english) {
  const normalised = normaliseGenderIdentity(value);
  if (!normalised) return '';
  const option = Q_CONSTANTS.genderIdentityOptions.find(
    (o) => o.value === normalised
  );
  return option ? t(option.label) : normalised;
}

function DateInput({ value, onChange, unsaved }) {
  const palette = changedPalette(useTheme());
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => !value && e.target.showPicker?.()}
      style={{
        ...changedInputStyle(palette, unsaved),
        width: 118,
        color: value ? 'inherit' : 'transparent'
      }}
    />
  );
}

function CellForColumn({
  col,
  member,
  entry,
  actions,
  ethnicityOptions,
  unsaved
}) {
  const t = useAutoTranslation();
  const palette = changedPalette(useTheme());

  switch (col.id) {
    case 'firstName':
      return (
        <input
          type="text"
          value={
            entry.firstName !== null ? entry.firstName : member.firstName || ''
          }
          placeholder={t('First name')}
          onChange={(e) =>
            actions.updatePersonField(member.pid, 'firstName', e.target.value)
          }
          style={changedInputStyle(palette, unsaved)}
        />
      );
    case 'lastName':
      return (
        <input
          type="text"
          value={
            entry.lastName !== null ? entry.lastName : member.lastName || ''
          }
          placeholder={t('Last name')}
          onChange={(e) =>
            actions.updatePersonField(member.pid, 'lastName', e.target.value)
          }
          style={changedInputStyle(palette, unsaved)}
        />
      );
    case 'gender': {
      const genderValue = normaliseGenderIdentity(
        entry.genderIdentity !== null
          ? entry.genderIdentity
          : member.genderIdentity ?? member.gender
      );
      const known = Q_CONSTANTS.genderIdentityOptions.map((o) => ({
        ...o,
        label: t(o.label)
      }));
      const options = Q_CONSTANTS.genderIdentityOptions.some(
        (o) => o.value === genderValue
      )
        ? known
        : [...known, { label: genderValue, value: genderValue }];
      const genderTitle = fullGenderLabel(genderValue, t);
      return (
        <Select
          size="small"
          variant="outlined"
          value={genderValue}
          onChange={(e) =>
            actions.updatePersonField(
              member.pid,
              'genderIdentity',
              e.target.value
            )
          }
          title={genderTitle || undefined}
          sx={{
            fontSize: 13,
            height: 32,
            width: '100%',
            '& .MuiSelect-select': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }
          }}
          renderValue={shortGenderLabel}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      );
    }
    case 'status': {
      const effectiveDod = entry.dod !== null ? entry.dod : member.dod || '';
      const statusValue =
        entry.isAlive !== null
          ? entry.isAlive
          : resolveVitalStatus({ isAlive: member.isAlive, dod: effectiveDod });
      return (
        <Select
          size="small"
          variant="outlined"
          value={statusValue}
          onChange={(e) =>
            actions.updatePersonFields(member.pid, {
              isAlive: e.target.value,
              ...(e.target.value === 'true' && effectiveDod ? { dod: '' } : {})
            })
          }
          sx={{
            fontSize: 13,
            height: 32,
            width: '100%'
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {t(opt.label)}
            </MenuItem>
          ))}
        </Select>
      );
    }
    case 'dob': {
      const dobValue = entry.dob !== null ? entry.dob : member.dob || '';
      return (
        <DateInput
          value={dobValue}
          unsaved={unsaved}
          onChange={(v) => actions.updatePersonField(member.pid, 'dob', v)}
        />
      );
    }
    case 'dod': {
      const dodValue = entry.dod !== null ? entry.dod : member.dod || '';
      return (
        <DateInput
          value={dodValue}
          unsaved={unsaved}
          onChange={(v) =>
            actions.updatePersonFields(
              member.pid,
              v ? { dod: v, isAlive: 'false' } : { dod: v }
            )
          }
        />
      );
    }
    case 'ethnicity': {
      const ethValue =
        entry.ethnicity !== null ? entry.ethnicity : member.ethnicity || [];
      return (
        <Autocomplete
          multiple
          size="small"
          options={ethnicityOptions}
          // An ethnicity from our fixed list is stored in English and
          // translated for display only, as the questionnaire's picker does; a
          // value from outside it is record data and shows as recorded.
          getOptionLabel={(option) =>
            ethnicityMap[option] ? t(ethnicityMap[option]) : option
          }
          value={ethValue}
          onChange={(_, val) =>
            actions.updatePersonField(member.pid, 'ethnicity', val)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={ethValue.length === 0 ? t('Ethnicity') : ''}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  sx: { fontSize: 12, py: '1px !important' }
                }
              }}
            />
          )}
          renderValue={(tags) => (
            <Typography variant="caption" noWrap>
              {tags
                .map((tag) => (ethnicityMap[tag] ? t(ethnicityMap[tag]) : tag))
                .join(', ')}
            </Typography>
          )}
          sx={{ width: '100%' }}
        />
      );
    }
    case 'mobile':
      return (
        <input
          type="text"
          value={entry.mobile !== null ? entry.mobile : member.mobile || ''}
          placeholder={t('Mobile')}
          onChange={(e) =>
            actions.updatePersonField(member.pid, 'mobile', e.target.value)
          }
          style={changedInputStyle(palette, unsaved)}
        />
      );
    case 'email':
      return (
        <input
          type="email"
          value={entry.email !== null ? entry.email : member.email || ''}
          placeholder={t('Email')}
          onChange={(e) =>
            actions.updatePersonField(member.pid, 'email', e.target.value)
          }
          style={changedInputStyle(palette, unsaved)}
        />
      );
    case 'hospitalNumber':
      return (
        <input
          type="text"
          value={
            entry.hospitalNumber !== null
              ? entry.hospitalNumber
              : member.hospitalNumber || ''
          }
          placeholder={t('Hospital Number')}
          onChange={(e) =>
            actions.updatePersonField(
              member.pid,
              'hospitalNumber',
              e.target.value
            )
          }
          style={changedInputStyle(palette, unsaved)}
        />
      );
    case 'healthcareId':
      return (
        <input
          type="text"
          value={
            entry.healthcareId !== null
              ? entry.healthcareId
              : member.healthcareId || ''
          }
          placeholder={t('Healthcare ID')}
          onChange={(e) =>
            actions.updatePersonField(
              member.pid,
              'healthcareId',
              e.target.value
            )
          }
          style={changedInputStyle(palette, unsaved)}
        />
      );
    case 'isHide': {
      const hideValue =
        entry.isHide !== null ? entry.isHide : Boolean(member.isHide);
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Checkbox
            size="small"
            checked={Boolean(hideValue)}
            onChange={(e) =>
              actions.updatePersonField(member.pid, 'isHide', e.target.checked)
            }
          />
        </Box>
      );
    }
    case 'recordsReviewed': {
      const reviewedValue =
        entry.recordsReviewed !== null
          ? entry.recordsReviewed
          : Boolean(member.recordsReviewed);
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Checkbox
            size="small"
            checked={Boolean(reviewedValue)}
            onChange={(e) =>
              actions.updatePersonField(
                member.pid,
                'recordsReviewed',
                e.target.checked
              )
            }
          />
        </Box>
      );
    }
    default:
      return null;
  }
}

export default function MemberRow({
  member,
  index,
  entry,
  isDirty,
  actions,
  familyId,
  visibleColumns,
  ethnicityOptions,
  colSpan
}) {
  const t = useAutoTranslation();
  const palette = changedPalette(useTheme());
  const [expanded, setExpanded] = useState(false);

  const displayId = member.upn ?? index + 1;
  // The columns holding an unsaved change: a draft that differs from the row.
  const changedKeys = new Set(changedPersonFields(entry, member));
  const changedColumns = new Set(
    Object.keys(COLUMN_DRAFT_KEYS).filter((id) =>
      COLUMN_DRAFT_KEYS[id].some((key) => changedKeys.has(key))
    )
  );
  // "Was:" for a column: the row's value as its cell shows it.
  const wasProps = (colId) => {
    const key = COLUMN_DRAFT_KEYS[colId]?.[0];
    const was = savedPersonValue(member, key);
    switch (colId) {
      case 'gender':
        return { was, formatWas: (v) => fullGenderLabel(v, t) || null };
      case 'status':
        return { was, options: STATUS_OPTIONS };
      case 'dob':
      case 'dod':
        return { was, kind: 'date' };
      case 'ethnicity':
        return {
          was,
          formatWas: (list) =>
            (list || [])
              .map((e) => (ethnicityMap[e] ? t(ethnicityMap[e]) : e))
              .join(', ') || null
        };
      case 'isHide':
      case 'recordsReviewed':
        return { was, kind: 'flag' };
      default:
        return { was };
    }
  };

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          borderLeft: isDirty
            ? `3px solid ${palette.main}`
            : '3px solid transparent'
        }}
      >
        {/* Expand toggle */}
        <TableCell padding="none" sx={{ width: 40, pl: 0.5 }}>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? (
              <RemoveIcon sx={{ fontSize: 16 }} />
            ) : (
              <AddIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </TableCell>

        {/* UPN */}
        <TableCell sx={{ width: 60, py: 0.75 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600
            }}
          >
            {displayId}
          </Typography>
        </TableCell>

        {/* Visible data columns */}
        {visibleColumns.map((col) => (
          <TableCell
            key={col.id}
            sx={{ py: 0.5, width: col.width, overflow: 'hidden' }}
          >
            {/* Each cell drawn and said as every changed field is (TGCLOUD-1902);
                its "Changed" is the row's dot, the cells being too small to hold one. */}
            <ChangedField
              name={`${member.pid}.${col.id}`}
              changed={changedColumns.has(col.id)}
              variant={CONTROL_COLUMNS.has(col.id) ? 'control' : 'field'}
              showLabel={false}
              {...wasProps(col.id)}
            >
              <CellForColumn
                col={col}
                member={member}
                entry={entry}
                actions={actions}
                ethnicityOptions={ethnicityOptions}
                unsaved={changedColumns.has(col.id)}
              />
            </ChangedField>
          </TableCell>
        ))}

        {/* Dirty indicator */}
        <TableCell sx={{ py: 0.5, width: 40, pr: 1 }}>
          {isDirty && (
            <FiberManualRecordIcon
              titleAccess={t('Unsaved changes in this row')}
              sx={{ fontSize: 10, color: palette.main }}
            />
          )}
        </TableCell>
      </TableRow>
      {/* Expanded clinical records panel */}
      {expanded && (
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ p: 0, borderBottom: 'none' }}>
            <ClinicalRecordsPanel
              member={member}
              entry={entry}
              actions={actions}
              familyId={familyId}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
